<template>
  <div class="dossier-op">
    <!-- <NavBar />
    <AuthBySessionExpired /> -->

    <div class="dossier-op__container">
      <!-- ─── Cabeçalho da página ─── -->
      <div class="dossier-op__header">
        <div class="dossier-op__header-left">
          <v-btn-previous-page v-if="false" />
          <div>
            <h1 class="dossier-op__title">
              <i :class="config.icon" class="mr-2"></i>
              {{ config.title }}
            </h1>
            <p class="dossier-op__subtitle">{{ config.subtitle }}</p>
          </div>
        </div>
        <v-btn class="btn-add" @click="openForm(null)">
          <i class="fas fa-plus mr-1"></i>
          {{ config.addLabel }}
        </v-btn>
      </div>

      <b-overlay :show="loading" opacity="0.99">
        <!-- ─── Painel de resumo ─── -->
        <div class="dossier-op__summary">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            class="dossier-op__summary-card"
            :class="[
              `dossier-op__summary-card--${tab.key}`,
              { 'dossier-op__summary-card--active': activeTab === tab.key },
            ]"
            @click="activeTab = tab.key"
          >
            <i :class="tab.icon" class="dossier-op__summary-icon"></i>
            <div>
              <span class="dossier-op__summary-number">{{
                countByTab(tab.key)
              }}</span>
              <span class="dossier-op__summary-label">{{ tab.label }}</span>
            </div>
          </div>
        </div>

        <!-- ─── Filtros ─── -->
        <div class="dossier-op__filters">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :placeholder="`Buscar ${config.itemName}`"
            dense
            outlined
            hide-details
            clearable
            class="dossier-op__search"
          />

          <v-select
            v-model="filterType"
            :items="config.typeOptions"
            dense
            outlined
            hide-details
            clearable
            :placeholder="`Todos os tipos`"
            class="dossier-op__filter-type"
          />

          <v-text-field
            v-model="filterResponsible"
            dense
            outlined
            hide-details
            clearable
            placeholder="Profissional responsável"
            class="dossier-op__filter-responsible"
          />
        </div>

        <!-- ─── Lista ─── -->
        <div v-show="filteredItems.length > 0" class="dossier-op__list">
          <div
            v-for="item in filteredItems"
            :key="item.id"
            class="dossier-op__card"
            :class="`dossier-op__card--${getStatus(item)}`"
          >
            <!-- Badge de status automático -->
            <div class="dossier-op__card-status-bar">
              <span
                class="dossier-op__status-badge"
                :class="`dossier-op__status-badge--${getStatus(item)}`"
              >
                <i :class="statusIcon(getStatus(item))" class="mr-1"></i>
                {{ statusLabel(getStatus(item)) }}
              </span>
              <span
                v-if="getStatus(item) === 'vigente'"
                class="dossier-op__days-left"
              >
                {{ daysLeft(item) }} dia{{
                  daysLeft(item) !== 1 ? "s" : ""
                }}
                restante{{ daysLeft(item) !== 1 ? "s" : "" }}
              </span>
              <span
                v-if="getStatus(item) === 'futuro'"
                class="dossier-op__days-to-start"
              >
                Inicia em {{ daysToStart(item) }} dia{{
                  daysToStart(item) !== 1 ? "s" : ""
                }}
              </span>
            </div>

            <!-- Conteúdo do card -->
            <div class="dossier-op__card-body">
              <div class="dossier-op__card-main">
                <!-- Nome e tipo -->
                <div class="dossier-op__card-name">{{ item.name }}</div>
                <div class="dossier-op__card-type">
                  <span class="dossier-op__type-pill">{{ item.type }}</span>
                  <span v-if="item.type_other" class="dossier-op__type-other">
                    – {{ item.type_other }}
                  </span>
                </div>

                <!-- Período de vigência -->
                <div class="dossier-op__card-period">
                  <i class="fas fa-calendar-alt mr-1"></i>
                  {{ formatDateBR(item.start_date) }}
                  <span class="mx-1">→</span>
                  {{ formatDateBR(item.end_date) }}
                </div>

                <!-- Responsável -->
                <div
                  v-if="item.responsible_name"
                  class="dossier-op__card-responsible"
                >
                  <i class="fas fa-user mr-1"></i>
                  {{ item.responsible_name }}
                  <span
                    v-if="item.responsible_council"
                    class="dossier-op__responsible-council"
                  >
                    · {{ item.responsible_council }}
                  </span>
                </div>
              </div>

              <!-- Arquivos anexados -->
              <div class="dossier-op__card-files">
                <div
                  v-for="(file, idx) in convertFiles(item.files)"
                  :key="idx"
                  class="dossier-op__file-item"
                >
                  <i class="fas fa-file-pdf dossier-op__file-icon"></i>
                  <span class="dossier-op__file-name">{{
                    file.name || `Arquivo ${idx + 1}`
                  }}</span>
                  <a
                    :href="file.url"
                    target="_blank"
                    class="dossier-op__file-link"
                  >
                    <i class="fas fa-eye mr-1"></i>Ver
                  </a>
                  <a
                    :href="file.url"
                    :download="file.name"
                    class="dossier-op__file-link ml-2"
                  >
                    <i class="fas fa-download mr-1"></i>Baixar
                  </a>
                </div>
                <div
                  v-if="!item.files || item.files.length === 0"
                  class="dossier-op__no-file"
                >
                  <i class="fas fa-exclamation-circle mr-1"></i>
                  Nenhum arquivo anexado
                </div>
              </div>
            </div>

            <!-- Observações -->
            <div v-if="item.observations" class="dossier-op__card-obs">
              <i class="fas fa-comment-alt mr-1"></i>
              {{ item.observations }}
            </div>

            <!-- Ações -->
            <div class="dossier-op__card-actions">
              <v-btn small text class="btn-certified" @click="openForm(item)">
                <i class="fas fa-pencil-alt mr-1"></i>Editar
              </v-btn>
              <v-btn
                small
                text
                class="btn-notcertified ml-2"
                @click="confirmDelete(item)"
              >
                <i class="fas fa-trash-alt mr-1"></i>Excluir
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Estado vazio -->
        <div v-show="filteredItems.length == 0" class="dossier-op__empty">
          <i :class="config.icon" class="dossier-op__empty-icon"></i>
          <p class="dossier-op__empty-text">
            Nenhum{{
              activeTab !== "todos"
                ? ` ${config.itemName} ${tabLabel(activeTab)}`
                : ` ${config.itemName} cadastrado`
            }}.
          </p>
          <v-btn class="btn-add mt-2" @click="openForm(null)">
            <i class="fas fa-plus mr-1"></i>{{ config.addLabel }}
          </v-btn>
        </div>
      </b-overlay>
    </div>

    <!-- ─── Modal de cadastro / edição ─── -->
    <b-modal
      :id="`modal-${config.moduleKey}`"
      :title="
        editingItem ? `Editar ${config.itemName}` : `Novo ${config.itemName}`
      "
      size="lg"
      no-close-on-backdrop
      hide-footer
    >
      <div v-if="showForm" class="dossier-op__form">
        <!-- Explicativo -->
        <div class="dossier-op__form-explainer">
          <i class="fas fa-info-circle mr-1"></i>
          {{ config.explainer }}
        </div>

        <!-- Nome -->
        <div class="dossier-op__form-field">
          <label
            class="dossier-op__form-label dossier-op__form-label--required"
          >
            Nome
          </label>
          <v-text-field
            v-model="form.name"
            dense
            outlined
            hide-details="auto"
            :placeholder="config.namePlaceholder"
            :rules="[rules.required]"
          />
        </div>

        <!-- Tipo -->
        <div class="dossier-op__form-row">
          <div class="dossier-op__form-field">
            <label
              class="dossier-op__form-label dossier-op__form-label--required"
            >
              Tipo
            </label>
            <v-select
              v-model="form.type"
              :items="config.typeOptions"
              dense
              outlined
              hide-details="auto"
              :rules="[rules.required]"
            />
          </div>

          <div
            v-if="form.type === 'Outros' || form.type === 'Outro'"
            class="dossier-op__form-field"
          >
            <label class="dossier-op__form-label">Especificar tipo</label>
            <v-text-field
              v-model="form.type_other"
              dense
              outlined
              hide-details
              placeholder="Descreva o tipo"
            />
          </div>
        </div>

        <!-- Responsável (CPF ou seleção) -->
        <div class="dossier-op__form-field">
          <label class="dossier-op__form-label">Profissional responsável</label>
          <div class="dossier-op__cpf-row">
            <v-text-field
              v-model="form.responsible_cpf"
              v-mask="'###.###.###-##'"
              dense
              outlined
              hide-details
              placeholder="000.000.000-00"
              :disabled="form._responsibleLocked"
              class="dossier-op__cpf-input"
              @blur="searchResponsible"
            />
            <v-btn
              v-if="!form._responsibleLocked"
              small
              class="btn-add ml-2"
              :loading="form._searchingResponsible"
              :disabled="!isCpfComplete(form.responsible_cpf)"
              @click="searchResponsible"
            >
              <i class="fas fa-search mr-1"></i>Buscar
            </v-btn>
            <v-btn
              v-else
              small
              class="btn-notcertified ml-2"
              @click="unlockResponsible"
            >
              <i class="fas fa-exchange-alt mr-1"></i>Trocar
            </v-btn>
          </div>
          <div
            v-if="form.responsible_name"
            class="dossier-op__responsible-preview"
          >
            <i class="fas fa-user-check mr-1"></i>
            <strong>{{ form.responsible_name }}</strong>
            <span v-if="form.responsible_council" class="ml-1">
              · {{ form.responsible_council }}
            </span>
          </div>
          <div v-if="form._responsibleNotFound" class="dossier-op__not-found">
            <i class="fas fa-info-circle mr-1"></i>
            Profissional não encontrado. Preencha o nome manualmente:
            <v-text-field
              v-model="form.responsible_name"
              dense
              outlined
              hide-details
              placeholder="Nome completo do responsável"
              class="mt-2"
            />
          </div>
        </div>

        <!-- Período de vigência -->
        <div class="dossier-op__form-row">
          <div class="dossier-op__form-field">
            <label
              class="dossier-op__form-label dossier-op__form-label--required"
            >
              Início da vigência
            </label>
            <v-text-field
              v-model="form.start_date"
              type="date"
              dense
              outlined
              hide-details="auto"
              :rules="[rules.required]"
            />
          </div>
          <div class="dossier-op__form-field">
            <label
              class="dossier-op__form-label dossier-op__form-label--required"
            >
              Fim da vigência
            </label>
            <v-text-field
              v-model="form.end_date"
              type="date"
              dense
              outlined
              hide-details="auto"
              :rules="[rules.required, rules.endAfterStart]"
            />
          </div>
        </div>

        <!-- Alerta de sobreposição de vigência -->
        <div v-if="overlapWarning" class="dossier-op__overlap-warning">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          {{ overlapWarning }}
        </div>

        <!-- Upload de arquivos (múltiplos) -->
        <div class="dossier-op__form-field">
          <label
            class="dossier-op__form-label dossier-op__form-label--required"
          >
            Arquivo(s) — PDF
          </label>
          <p class="dossier-op__form-hint">
            Você pode anexar múltiplos arquivos (ex.: um por turno ou setor).
          </p>

          <!-- Arquivos já anexados -->
          <div
            v-for="(file, idx) in convertFiles(form.files)"
            :key="idx"
            class="dossier-op__form-file-item"
          >
            <i class="fas fa-file-pdf dossier-op__file-icon"></i>
            <v-text-field
              v-model="file.name"
              dense
              outlined
              hide-details
              placeholder="Nome do arquivo"
              class="dossier-op__file-name-input mx-2"
            />
            <a
              v-if="file.url"
              :href="file.url"
              target="_blank"
              class="dossier-op__file-link mr-2"
            >
              <i class="fas fa-eye"></i>
            </a>
            <v-btn
              icon
              x-small
              class="btn-notcertified"
              @click="removeFile(idx)"
            >
              <i class="fas fa-times"></i>
            </v-btn>
          </div>

          <!-- Botão adicionar arquivo -->
          <v-btn
            small
            class="btn-add mt-2"
            :loading="uploadLoading"
            @click="$refs.fileInput.click()"
          >
            <i class="fas fa-upload mr-1"></i>
            {{
              form.files.length > 0
                ? "Adicionar outro arquivo"
                : "Anexar arquivo"
            }}
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            accept="application/pdf"
            style="display: none"
            @change="handleFileUpload"
          />
        </div>

        <!-- Observações -->
        <div class="dossier-op__form-field">
          <label class="dossier-op__form-label">Observações</label>
          <v-textarea
            v-model="form.observations"
            dense
            outlined
            rows="2"
            auto-grow
            hide-details
            placeholder="Informações complementares (opcional)"
          />
        </div>

        <!-- Ações do formulário -->
        <div class="dossier-op__form-actions">
          <v-btn text @click="closeForm">Cancelar</v-btn>
          <v-btn class="btn-add ml-2" :loading="saveLoading" @click="save">
            <i class="fas fa-save mr-1"></i>
            {{ editingItem ? "Salvar alterações" : "Cadastrar" }}
          </v-btn>
        </div>

        <ErrorsMessages :errors="formErrors" />
      </div>
    </b-modal>

    <!-- Modal de confirmação de exclusão -->
    <b-modal
      :id="`modal-delete-${config.moduleKey}`"
      title="Confirmar exclusão"
      size="sm"
      no-close-on-backdrop
      hide-footer
    >
      <div v-if="deletingItem">
        <p class="text-fourteen">
          Deseja excluir <strong>{{ deletingItem.name }}</strong
          >? Esta ação não pode ser desfeita.
        </p>
        <div class="dossier-op__form-actions">
          <v-btn text @click="$bvModal.hide(`modal-delete-${config.moduleKey}`)"
            >Cancelar</v-btn
          >
          <v-btn
            class="btn-notcertified ml-2"
            :loading="deleteLoading"
            @click="deleteItem"
          >
            <i class="fas fa-trash-alt mr-1"></i>Excluir
          </v-btn>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
// import NavBar from "../../../templates/NavBar";
// import AuthBySessionExpired from "../../../templates/Components/AuthBySessionExpired";
import VBtnPreviousPage from "../../../templates/Components/VBtnPreviousPage.vue";
import ErrorsMessages from "../../../templates/Components/ErrorsMessages";

import api from "../../../services/api";
import toast from "../../../lib/ToastNotification";
import permission from "../../../lib/permission";

const emptyForm = () => ({
  id: null,
  name: "",
  type: null,
  type_other: "",
  responsible_cpf: "",
  responsible_name: "",
  responsible_council: "",
  responsible_user_id: null,
  start_date: null,
  end_date: null,
  files: [],
  observations: "",
  // campos UI
  _responsibleLocked: false,
  _responsibleNotFound: false,
  _searchingResponsible: false,
});

export default {
  name: "DossierOperationalBase",

  components: {
    // NavBar,
    // AuthBySessionExpired,
    VBtnPreviousPage,
    ErrorsMessages,
  },

  // ── config define tudo que difere entre Escala e Cardápio ─────────────────
  props: {
    config: {
      type: Object,
      required: true,
      // Formato esperado: {
      //   moduleKey, title, subtitle, icon,
      //   itemName, addLabel, namePlaceholder, explainer,
      //   typeOptions, writePermission, readPermission,
      //   saveEndpoint, uploadEndpoint, listEndpoint, deleteEndpoint,
      // }
    },
  },

  data() {
    return {
      loading: false,
      saveLoading: false,
      deleteLoading: false,
      uploadLoading: false,

      items: [],
      activeTab: "vigentes",

      search: "",
      filterType: null,
      filterResponsible: "",

      showForm: false,
      editingItem: null,
      deletingItem: null,
      form: emptyForm(),
      formErrors: [],

      tabs: [
        { key: "vigentes", label: "Vigentes", icon: "fas fa-check-circle" },
        { key: "futuros", label: "Futuros", icon: "fas fa-clock" },
        { key: "anteriores", label: "Anteriores", icon: "fas fa-history" },
        { key: "todos", label: "Todos", icon: "fas fa-list" },
      ],

      rules: {
        required: (v) => !!v || "Campo obrigatório",
        endAfterStart: (v) => {
          if (!v || !this.form.start_date) return true;
          return (
            v >= this.form.start_date ||
            "A data de fim deve ser posterior ao início"
          );
        },
      },
    };
  },

  computed: {
    // ── Status automático ──────────────────────────────────────────────────
    today() {
      return new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    },

    filteredItems() {
      let list = this.items;

      // Filtro por aba
      if (this.activeTab !== "todos") {
        list = list.filter(
          (item) => this.getStatus(item) === this.activeTab.slice(0, -1)
        ); // remove 's'
        // "vigentes" → "vigente" | "futuros" → "futuro" | "anteriores" → "anterior"
        const map = {
          vigentes: "vigente",
          futuros: "futuro",
          anteriores: "anterior",
        };
        list = this.items.filter(
          (item) => this.getStatus(item) === map[this.activeTab]
        );
      }

      // Filtro por busca
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          (item) =>
            item.name.toLowerCase().includes(q) ||
            (item.responsible_name || "").toLowerCase().includes(q)
        );
      }

      // Filtro por tipo
      if (this.filterType) {
        list = list.filter((item) => item.type === this.filterType);
      }

      // Filtro por responsável
      if (this.filterResponsible) {
        const q = this.filterResponsible.toLowerCase();
        list = list.filter((item) =>
          (item.responsible_name || "").toLowerCase().includes(q)
        );
      }

      return list;
    },

    overlapWarning() {
      if (!this.form.start_date || !this.form.end_date || !this.form.type)
        return null;
      const overlapping = this.items.filter((item) => {
        if (this.editingItem && item.id === this.editingItem.id) return false;
        if (item.type !== this.form.type) return false;
        // Verifica sobreposição de períodos
        return (
          this.form.start_date <= item.end_date &&
          this.form.end_date >= item.start_date
        );
      });
      if (!overlapping.length) return null;
      return `Atenção: já existe um ${this.config.itemName} do tipo "${this.form.type}" com vigência sobreposta a este período ("${overlapping[0].name}"). Verifique antes de salvar.`;
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    permission,

    convertFiles(string = "[]") {
      let files = [];

      if (typeof string === "string") {
        files = JSON.parse(string);
      } else {
        files = string;
      }

      return files;
    },

    // ── Busca ──────────────────────────────────────────────────────────────
    async retrieve() {
      try {
        this.loading = true;
        const response = await api.get(this.config.listEndpoint);
        const { error, items } = response.data;
        if (!error) this.items = items || [];
      } catch (err) {
        console.error("DossierOperationalBase.retrieve:", err);
      } finally {
        this.loading = false;
      }
    },

    // ── Status automático por item ─────────────────────────────────────────
    getStatus(item) {
      const today = this.today;
      if (item.start_date > today) return "futuro";
      if (item.end_date < today) return "anterior";
      return "vigente";
    },

    countByTab(tabKey) {
      const map = {
        vigentes: "vigente",
        futuros: "futuro",
        anteriores: "anterior",
        todos: null,
      };
      if (!map[tabKey]) return this.items.length;
      return this.items.filter((item) => this.getStatus(item) === map[tabKey])
        .length;
    },

    daysLeft(item) {
      const end = new Date(item.end_date);
      const today = new Date(this.today);
      return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    },

    daysToStart(item) {
      const start = new Date(item.start_date);
      const today = new Date(this.today);
      return Math.ceil((start - today) / (1000 * 60 * 60 * 24));
    },

    statusLabel(status) {
      return (
        { vigente: "Vigente", futuro: "Futuro", anterior: "Anterior" }[
          status
        ] || status
      );
    },

    statusIcon(status) {
      return (
        {
          vigente: "fas fa-check-circle",
          futuro: "fas fa-clock",
          anterior: "fas fa-history",
        }[status] || "far fa-circle"
      );
    },

    tabLabel(tabKey) {
      return (
        { vigentes: "vigente", futuros: "futuro", anteriores: "anterior" }[
          tabKey
        ] || tabKey
      );
    },

    // ── Formulário ─────────────────────────────────────────────────────────
    openForm(item) {
      this.editingItem = item;
      this.formErrors = [];
      if (item) {
        this.form = {
          ...emptyForm(),
          ...item,
          files: JSON.parse(JSON.stringify(item.files || [])),
          _responsibleLocked: !!item.responsible_cpf,
        };
      } else {
        this.form = emptyForm();
      }
      this.showForm = true;
      this.$bvModal.show(`modal-${this.config.moduleKey}`);
    },

    closeForm() {
      this.showForm = false;
      this.editingItem = null;
      this.formErrors = [];
      this.$bvModal.hide(`modal-${this.config.moduleKey}`);
    },

    // ── Busca responsável por CPF ──────────────────────────────────────────
    isCpfComplete(cpf) {
      return (cpf || "").replace(/\D/g, "").length === 11;
    },

    async searchResponsible() {
      if (!this.isCpfComplete(this.form.responsible_cpf)) return;
      if (this.form._responsibleLocked) return;

      this.form._searchingResponsible = true;
      this.form._responsibleNotFound = false;

      try {
        const cpf = this.form.responsible_cpf.replace(/\D/g, "");
        const response = await api.get("/rest-home/user/find-by-cpf", {
          params: { cpf },
        });
        const { error, user } = response.data;

        if (error || !user) {
          this.form._responsibleNotFound = true;
          this.form._responsibleLocked = true;
        } else {
          this.form.responsible_name = user.name || "";
          this.form.responsible_council =
            user.professional_class_council_value || "";
          this.form.responsible_user_id = user.id;
          this.form._responsibleLocked = true;
          this.form._responsibleNotFound = false;
        }
      } catch (err) {
        console.error("DossierOperationalBase.searchResponsible:", err);
      } finally {
        this.form._searchingResponsible = false;
      }
    },

    unlockResponsible() {
      this.form.responsible_cpf = "";
      this.form.responsible_name = "";
      this.form.responsible_council = "";
      this.form.responsible_user_id = null;
      this.form._responsibleLocked = false;
      this.form._responsibleNotFound = false;
    },

    // ── Upload de arquivos (múltiplos) ─────────────────────────────────────
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.type !== "application/pdf") {
        toast("Apenas PDFs são aceitos.", "error");
        return;
      }

      this.uploadLoading = true;
      try {
        const fd = new FormData();
        fd.append(
          "file",
          new Blob([file], { type: "application/pdf" }),
          file.name
        );

        const response = await api.post(this.config.uploadEndpoint, fd);
        const { error, file_url, file_name } = response.data;
        if (!error) {
          this.form.files.push({ url: file_url, name: file_name || file.name });
          toast("Arquivo anexado com sucesso", "success");
        }
      } catch (err) {
        console.error("DossierOperationalBase.upload:", err);
      } finally {
        this.uploadLoading = false;
        event.target.value = "";
      }
    },

    removeFile(idx) {
      this.form.files.splice(idx, 1);
    },

    // ── Salvar ─────────────────────────────────────────────────────────────
    async save() {
      this.formErrors = [];

      // Validação básica
      if (
        !this.form.name ||
        !this.form.type ||
        !this.form.start_date ||
        !this.form.end_date
      ) {
        this.formErrors = [
          { message: "Preencha todos os campos obrigatórios." },
        ];
        return;
      }

      try {
        this.saveLoading = true;

        // Remove campos UI antes de enviar
        const payload = { ...this.form };
        [
          "_responsibleLocked",
          "_responsibleNotFound",
          "_searchingResponsible",
        ].forEach((k) => delete payload[k]);

        const isEdit = !!this.editingItem;
        const response = isEdit
          ? await api.put(this.config.saveEndpoint, payload)
          : await api.post(this.config.saveEndpoint, payload);

        const { error, errors, message } = response.data;

        if (error) {
          this.formErrors = errors;
        } else {
          toast(
            message ||
              (isEdit ? "Atualizado com sucesso" : "Cadastrado com sucesso"),
            "success"
          );
          this.closeForm();
          await this.retrieve();
        }
      } catch (err) {
        console.error("DossierOperationalBase.save:", err);
      } finally {
        this.saveLoading = false;
      }
    },

    // ── Excluir ────────────────────────────────────────────────────────────
    confirmDelete(item) {
      this.deletingItem = item;
      this.$bvModal.show(`modal-delete-${this.config.moduleKey}`);
    },

    async deleteItem() {
      try {
        this.deleteLoading = true;
        const response = await api.delete(this.config.deleteEndpoint, {
          params: { id: this.deletingItem.id },
        });
        const { error, message } = response.data;
        if (!error) {
          toast(message || "Excluído com sucesso", "success");
          this.$bvModal.hide(`modal-delete-${this.config.moduleKey}`);
          this.deletingItem = null;
          await this.retrieve();
        }
      } catch (err) {
        console.error("DossierOperationalBase.delete:", err);
      } finally {
        this.deleteLoading = false;
      }
    },

    // ── Utilitários ────────────────────────────────────────────────────────
    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER OPERATIONAL BASE
   Compartilhado por WorkSchedule e Menu
   ═══════════════════════════════════════════════════════════════ */

.dossier-op {
  min-height: 100vh;
  background: #f8f8f7;
}

.dossier-op__container {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 20px 40px;
}

/* ── Cabeçalho ───────────────────────────────────────────────── */
.dossier-op__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 0 16px;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 20px;
}

.dossier-op__header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.dossier-op__title {
  font-size: 20px;
  font-weight: 600;
  color: #33495e;
  margin: 0 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.dossier-op__subtitle {
  font-size: 13px;
  color: #777;
  margin: 0;
}

/* ── Painel de resumo ────────────────────────────────────────── */
.dossier-op__summary {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.dossier-op__summary-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background: #fff;
  flex: 1;
  min-width: 110px;
  cursor: pointer;
  transition: all 0.15s;
}

.dossier-op__summary-card:hover {
  border-color: #1f5a76;
}

.dossier-op__summary-card--active {
  border-color: #1f5a76;
  background: #eef6fb;
}

.dossier-op__summary-icon {
  font-size: 20px;
  flex-shrink: 0;
}
.dossier-op__summary-number {
  display: block;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  font-family: "WorkSans-SemiBold", sans-serif;
}
.dossier-op__summary-label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #666;
  margin-top: 2px;
}

.dossier-op__summary-card--vigentes .dossier-op__summary-icon,
.dossier-op__summary-card--vigentes .dossier-op__summary-number {
  color: #30915b;
}
.dossier-op__summary-card--futuros .dossier-op__summary-icon,
.dossier-op__summary-card--futuros .dossier-op__summary-number {
  color: #1f5a76;
}
.dossier-op__summary-card--anteriores .dossier-op__summary-icon,
.dossier-op__summary-card--anteriores .dossier-op__summary-number {
  color: #888;
}
.dossier-op__summary-card--todos .dossier-op__summary-icon,
.dossier-op__summary-card--todos .dossier-op__summary-number {
  color: #444;
}

/* ── Filtros ─────────────────────────────────────────────────── */
.dossier-op__filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.dossier-op__search {
  flex: 1;
  min-width: 180px;
}
.dossier-op__filter-type {
  flex: 0 1 200px;
}
.dossier-op__filter-responsible {
  flex: 0 1 220px;
}

/* ── Cards de item ───────────────────────────────────────────── */
.dossier-op__card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 10px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.dossier-op__card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}

.dossier-op__card--vigente {
  border-left: 4px solid #30915b;
}
.dossier-op__card--futuro {
  border-left: 4px solid #1f5a76;
}
.dossier-op__card--anterior {
  border-left: 4px solid #ccc;
  opacity: 0.85;
}

/* ── Barra de status ─────────────────────────────────────────── */
.dossier-op__card-status-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: #f8f8f7;
  border-bottom: 1px solid #f0f0ef;
}

.dossier-op__status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.dossier-op__status-badge--vigente {
  background: #d4edda;
  color: #155724;
}
.dossier-op__status-badge--futuro {
  background: #cce5ff;
  color: #004085;
}
.dossier-op__status-badge--anterior {
  background: #f0f0f0;
  color: #666;
}

.dossier-op__days-left {
  font-size: 11px;
  color: #30915b;
  font-weight: 600;
}
.dossier-op__days-to-start {
  font-size: 11px;
  color: #1f5a76;
  font-weight: 600;
}

/* ── Corpo do card ───────────────────────────────────────────── */
.dossier-op__card-body {
  display: flex;
  gap: 20px;
  padding: 12px 16px;
  flex-wrap: wrap;
}
.dossier-op__card-main {
  flex: 1;
  min-width: 0;
}

.dossier-op__card-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}
.dossier-op__card-type {
  margin-bottom: 4px;
}

.dossier-op__type-pill {
  font-size: 11px;
  font-weight: 600;
  background: #e8f4fd;
  color: #1f5a76;
  border: 1px solid #b8d9ec;
  border-radius: 10px;
  padding: 1px 8px;
}
.dossier-op__type-other {
  font-size: 12px;
  color: #666;
}

.dossier-op__card-period,
.dossier-op__card-responsible {
  font-size: 12px;
  color: #555;
  margin-top: 3px;
}

.dossier-op__responsible-council {
  color: #888;
}

/* ── Arquivos no card ────────────────────────────────────────── */
.dossier-op__card-files {
  flex: 0 1 260px;
}

.dossier-op__file-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background: #f8f8f7;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 4px;
}
.dossier-op__file-icon {
  font-size: 14px;
  color: #dc3545;
  flex-shrink: 0;
}
.dossier-op__file-name {
  font-size: 12px;
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dossier-op__file-link {
  font-size: 12px;
  color: #1f5a76;
  font-weight: 600;
  flex-shrink: 0;
}

.dossier-op__no-file {
  font-size: 12px;
  color: #aaa;
  font-style: italic;
  padding: 6px 0;
}

/* ── Observações ─────────────────────────────────────────────── */
.dossier-op__card-obs {
  font-size: 12px;
  color: #666;
  font-style: italic;
  padding: 6px 16px 8px;
  border-top: 1px solid #f4f4f4;
}

/* ── Ações do card ───────────────────────────────────────────── */
.dossier-op__card-actions {
  display: flex;
  align-items: center;
  padding: 6px 12px 10px;
  border-top: 1px solid #f4f4f4;
}

/* ── Estado vazio ────────────────────────────────────────────── */
.dossier-op__empty {
  text-align: center;
  padding: 48px 24px;
  color: #aaa;
}
.dossier-op__empty-icon {
  font-size: 48px;
  display: block;
  color: #ddd;
  margin-bottom: 12px;
}
.dossier-op__empty-text {
  font-size: 14px;
  color: #888;
  margin: 0;
}

/* ── Formulário (modal) ──────────────────────────────────────── */
.dossier-op__form {
  padding: 4px 0;
}

.dossier-op__form-explainer {
  font-size: 12px;
  color: #555;
  background: #f8f8f7;
  border-left: 3px solid #1f5a76;
  padding: 8px 10px;
  border-radius: 0 4px 4px 0;
  margin-bottom: 14px;
}

.dossier-op__form-field {
  margin-bottom: 12px;
}
.dossier-op__form-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.dossier-op__form-row .dossier-op__form-field {
  flex: 1;
  min-width: 180px;
}

.dossier-op__form-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.dossier-op__form-label--required::after {
  content: " *";
  color: #dc3545;
}
.dossier-op__form-hint {
  font-size: 12px;
  color: #888;
  margin: 0 0 6px;
}

/* CPF responsável */
.dossier-op__cpf-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.dossier-op__cpf-input {
  flex: 0 0 200px;
}

.dossier-op__responsible-preview {
  margin-top: 6px;
  font-size: 13px;
  color: #1f5a76;
  padding: 6px 8px;
  background: #eef6fb;
  border: 1px solid #b8d9ec;
  border-radius: 4px;
}

.dossier-op__not-found {
  margin-top: 8px;
  font-size: 13px;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 8px 10px;
}

/* Arquivos no formulário */
.dossier-op__form-file-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0ef;
  margin-bottom: 4px;
}
.dossier-op__file-name-input {
  font-size: 12px;
}

/* Sobreposição */
.dossier-op__overlap-warning {
  font-size: 13px;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 12px;
}

/* Ações do form */
.dossier-op__form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0ef;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-op__header {
    flex-direction: column;
  }
  .dossier-op__filters {
    flex-direction: column;
  }
  .dossier-op__search,
  .dossier-op__filter-type,
  .dossier-op__filter-responsible {
    flex: unset;
    width: 100%;
  }
  .dossier-op__card-body {
    flex-direction: column;
  }
  .dossier-op__card-files {
    flex: unset;
    width: 100%;
  }
  .dossier-op__summary-card {
    min-width: calc(50% - 8px);
    flex: unset;
  }
  .dossier-op__cpf-input {
    flex: unset;
    width: 100%;
  }
}
</style>